export const ROOM_COLORS = {
  classroom: '#E9D8C4', office: '#DCD6CC', corridor: '#C9C3BA', wet: '#BFD3DB', technical: '#B9B3A8',
  hall: '#E4CDB2', kitchen: '#E7D3BA', stair: '#A9A39A', living: '#E9D8C4', bedroom: '#E2D5C6', storage: '#CFC8BE', other: '#DDD5CA',
};

export const ROOM_LABELS = {
  classroom: 'Undervisning', office: 'Kontor', corridor: 'Kommunikation', wet: 'Våtrum', technical: 'Teknik',
  hall: 'Samlingsyta', kitchen: 'Kök', stair: 'Trapphus', living: 'Vardagsrum', bedroom: 'Sovrum', storage: 'Förråd', other: 'Övrigt',
};

export const LAYERS = [
  { key: 'foundation', label: 'Grundläggning' },
  { key: 'slabs', label: 'Bjälklag' },
  { key: 'extWalls', label: 'Ytterväggar' },
  { key: 'intWalls', label: 'Innerväggar' },
  { key: 'windows', label: 'Fönster & dörrar' },
  { key: 'roof', label: 'Tak' },
  { key: 'rooms', label: 'Rum & ytor' },
];

const PHASE_RULES = [
  [/^\W*\d*\.?\s*(mark|schakt|sprängning|finplan)/, null],
  [/grund|platta|pål|källare/, 'foundation'],
  [/tak|yttertak/, 'roof'],
  [/fönster|dörr|glas|parti/, 'windows'],
  [/fasad|yttervägg|klimatskal|tegel|puts/, 'extWalls'],
  [/innervägg|mellanvägg|gips|inner|rumsbild/, 'intWalls'],
  [/stomme|bjälklag|betong|stål|bärande/, 'slabs'],
  [/ytskikt|golv|måln|kakel|inredning|snickeri|install|\bel\b|vvs|vent|finplan/, 'rooms'],
];

export const phaseToLayer = (name = '') => {
  const n = name.toLowerCase();
  const hit = PHASE_RULES.find(([re]) => re.test(n));
  return hit ? hit[1] : null;
};

const near = (a, b) => Math.abs(a - b) < 0.25;

const mergeIntervals = (segs) => {
  const sorted = [...segs].sort((a, b) => a[0] - b[0]);
  const out = [];
  sorted.forEach(([s, e]) => {
    const last = out[out.length - 1];
    if (last && s <= last[1] + 0.05) last[1] = Math.max(last[1], e);
    else out.push([s, e]);
  });
  return out;
};

// Innerväggar per plan: rumskanter som inte ligger på fasaden, sammanslagna per linje
export const interiorWalls = (rooms, W, D) => {
  const lines = {};
  const add = (orient, c, s, e) => {
    const key = `${orient}:${Math.round(c * 10) / 10}`;
    (lines[key] = lines[key] || []).push([s, e]);
  };
  rooms.forEach((r) => {
    [[r.y, r.x, r.x + r.w], [r.y + r.d, r.x, r.x + r.w]].forEach(([c, s, e]) => { if (!near(c, 0) && !near(c, D)) add('h', c, s, e); });
    [[r.x, r.y, r.y + r.d], [r.x + r.w, r.y, r.y + r.d]].forEach(([c, s, e]) => { if (!near(c, 0) && !near(c, W)) add('v', c, s, e); });
  });
  const walls = [];
  Object.entries(lines).forEach(([key, segs]) => {
    const [orient, c] = key.split(':');
    mergeIntervals(segs).forEach(([s, e]) => walls.push({ orient, c: parseFloat(c), s, e }));
  });
  return walls;
};

// Fönster längs fasaden (var ~3.2 m), hoppar över hörn
export const facadeWindows = (W, D, spacing = 3.2) => {
  const out = [];
  const along = (len, fn) => {
    const n = Math.max(1, Math.floor((len - 1.6) / spacing));
    const start = (len - (n - 1) * spacing) / 2;
    for (let i = 0; i < n; i += 1) out.push(fn(start + i * spacing));
  };
  along(W, (t) => ({ x: t, y: 0, rot: 0 }));
  along(W, (t) => ({ x: t, y: D, rot: 0 }));
  along(D, (t) => ({ x: 0, y: t, rot: 1 }));
  along(D, (t) => ({ x: W, y: t, rot: 1 }));
  return out;
};

export const wingsOf = (model) => (model.wings?.length
  ? model.wings
  : [{ name: 'Byggnad', x: 0, y: 0, w: model.footprint.width, d: model.footprint.depth, floors: model.floors.length, roof: model.roof }]);

export const inWings = (wings, x, y, minFloors = 1, skip = null) =>
  wings.some((w) => w !== skip && w.floors >= minFloors && x > w.x + 0.05 && x < w.x + w.w - 0.05 && y > w.y + 0.05 && y < w.y + w.d - 0.05);

export const defaultFacade = (model) => model.facade || (model.roof?.type === 'gable' ? 'wood' : 'brick');

// Fönsterpositioner per vinge och plan. Hoppar över fasadavsnitt som ligger mot en annan volym.
export const wingWindows = (wing, wings, floorIdx, spacing = 3.2) => {
  const out = [];
  const sides = [
    { len: wing.w, at: (t) => [wing.x + t, wing.y], n: [0, -1], rot: 0 },
    { len: wing.w, at: (t) => [wing.x + t, wing.y + wing.d], n: [0, 1], rot: 0 },
    { len: wing.d, at: (t) => [wing.x, wing.y + t], n: [-1, 0], rot: 1 },
    { len: wing.d, at: (t) => [wing.x + wing.w, wing.y + t], n: [1, 0], rot: 1 },
  ];
  sides.forEach((s, si) => {
    const n = Math.max(1, Math.floor((s.len - 1.2) / spacing));
    const start = (s.len - (n - 1) * spacing) / 2;
    for (let i = 0; i < n; i += 1) {
      const [x, y] = s.at(start + i * spacing);
      if (inWings(wings, x + s.n[0] * 0.6, y + s.n[1] * 0.6, floorIdx + 1, wing)) continue;
      out.push({ x, y, rot: s.rot, nx: s.n[0], ny: s.n[1], side: si, i });
    }
  });
  return out;
};

export const modelStats = (model) => {
  if (!model) return null;
  const { width: W, depth: D } = model.footprint;
  const floors = model.floors.length;
  const wings = wingsOf(model);
  const rooms = model.floors.reduce((s, f) => s + f.rooms.length, 0);
  const roomArea = model.floors.reduce((s, f) => s + f.rooms.reduce((a, r) => a + r.w * r.d, 0), 0);
  const bta = wings.reduce((s, w) => s + w.w * w.d * w.floors, 0);
  return { W, D, floors, rooms, footprint: wings.reduce((s, w) => s + w.w * w.d, 0), bta, roomArea, volume: bta * model.floor_height, height: floors * model.floor_height };
};
